import { Component, forwardRef, input, signal } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { CharacterCountComponent } from "../character-count/character-count.component";
import { CONSTANTS } from "../../../constants";
import { ButtonModule } from "primeng/button";

/**
 * Standardized multi-line text input with an optional character counter.
 *
 * Implements `ControlValueAccessor`, so it drops into reactive forms with
 * `formControlName`. Pairs with `app-form-field` for the label/error, and
 * reuses `app-character-count` for the footer counter. Optional `quickTapOptions`
 * always appear when configured and insert repeatable plain text at the cursor,
 * followed by a space.
 */
@Component({
  selector: "app-textarea",
  standalone: true,
  imports: [CharacterCountComponent, ButtonModule],
  templateUrl: "./textarea.component.html",
  styleUrl: "./textarea.component.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true
    }
  ]
})
export class TextareaComponent implements ControlValueAccessor {
  inputId = input<string>("");
  placeholder = input<string>("");
  maxLength = input<number>(CONSTANTS.CONSTRAINTS.MAX_NOTE_LENGTH);
  rows = input<number>(4);
  /** Show the `x / max` counter beneath the field. */
  showCount = input<boolean>(true);
  /** Force invalid styling when used without `app-form-field`. */
  invalid = input<boolean>(false);
  testId = input<string>("app-textarea");
  quickTapOptions = input<readonly string[]>([]);

  protected readonly value = signal<string>("");
  protected readonly disabled = signal<boolean>(false);

  private onChange: (value: string) => void = () => { /* noop until registered */ };
  private onTouched: () => void = () => { /* noop until registered */ };

  writeValue(value: string): void {
    this.value.set(value ?? "");
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected handleInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.value.set(target.value);
    this.onChange(target.value);
  }

  protected handleBlur(): void {
    this.onTouched();
  }

  protected quickTapText(option: string, textarea: HTMLTextAreaElement): string {
    const before = this.value().slice(0, textarea.selectionStart);
    return `${before && !/\s$/.test(before) ? " " : ""}${option} `;
  }

  protected insertQuickTap(option: string, textarea: HTMLTextAreaElement): void {
    const text = this.quickTapText(option, textarea);
    if (this.disabled() || this.value().length + text.length > this.maxLength()) {
      return;
    }

    const position = textarea.selectionStart;
    const value = this.value();
    const nextValue = value.slice(0, position) + text + value.slice(position);
    this.value.set(nextValue);
    textarea.value = nextValue;
    this.onChange(nextValue);
    textarea.focus({ preventScroll: true });
    textarea.setSelectionRange(position + text.length, position + text.length);
  }
}
